import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import config from '../config';
import './Sidebar.css';

const PREFS_URL  = `${config.apiBase}/preferences.php`;
const UPLOAD_URL = `${config.apiBase}/upload_avatar.php`;
const AVATAR_BASE = config.apiBase.replace('/api', '');

const Sidebar = ({ user, setUser }) => {
  const role = String(user?.role || '').toLowerCase();
  const isAdmin = ['admin', 'superadmin'].includes(role);
  const isSuperAdmin = role === 'superadmin';
  const allowedApps = isAdmin ? config.apps : config.apps.filter(app => app.key === 'planning');
  const [isCollapsed,  setIsCollapsed]  = useState(() => window.innerWidth <= 768);
  const [imgKey,       setImgKey]       = useState(Date.now());
  const [showSettings, setShowSettings] = useState(false);
  const [visibleApps,  setVisibleApps]  = useState(allowedApps.map(a => a.key));

  const navigate = useNavigate();

  const userLogin = user?.[config.loginField] || user?.username || user?.login || '';
  const userName  = user?.name || user?.nom || userLogin || 'Utilisateur';

  const avatarUrl  = userLogin ? `${AVATAR_BASE}/assets/avatars/${userLogin}.jpg` : '';
  const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=e32636&color=fff`;

  // Charger les préférences
  useEffect(() => {
    if (!userLogin) return;

    const authToken = localStorage.getItem(config.tokenKey) || '';
    fetch(`${PREFS_URL}?login=${encodeURIComponent(userLogin)}`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then(r => r.json())
      .then(data => {
        if (!data.success || !data.prefs) return;

        if (Array.isArray(data.prefs.visibleApps)) {
          const filtered = data.prefs.visibleApps.filter(k => allowedApps.some(a => a.key === k));
          if (filtered.length) setVisibleApps(filtered);
          return;
        }

        // Fallback format objet { key: true/false }
        const apps = Object.keys(data.prefs)
          .filter(k => data.prefs[k] === true && allowedApps.some(a => a.key === k));
        if (apps.length) setVisibleApps(apps);
      })
      .catch(err => console.error('Erreur prefs', err));
  }, [userLogin, isAdmin]);

  const savePreferences = async (newList) => {
    if (!userLogin) return;
    try {
      await fetch(PREFS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(config.tokenKey) || ''}`
        },
        body: JSON.stringify({ login: userLogin, prefs: { visibleApps: newList } })
      });
    } catch (err) {
      console.error('Erreur save prefs', err);
    }
  };

  const toggleApp = (key) => {
    const next = visibleApps.includes(key)
      ? visibleApps.filter(k => k !== key)
      : [...visibleApps, key];
    setVisibleApps(next);
    savePreferences(next);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !userLogin) return;

    const formData = new FormData();
    formData.append('avatar', file);
    formData.append('login', userLogin);

    try {
      const res  = await fetch(UPLOAD_URL, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setImgKey(Date.now());
        alert('Photo mise à jour !');
      }
    } catch (err) {
      console.error('Erreur upload', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(config.tokenKey);
    localStorage.removeItem(config.userKey);
    setUser(null);
    navigate('/login');
  };

  return (
    <>
      <aside className={`stat-card sidebar-container ${isCollapsed ? 'collapsed' : ''}`}>

        <button
          className="toggle-btn"
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? 'Ouvrir le menu' : 'Fermer le menu'}
        >
          {isCollapsed ? '➡' : '⬅'}
        </button>

        <div className="sidebar-header">
          {!isCollapsed && (
            <img className="sidebar-brand-logo" src="/logo-isolacier.png" alt="Isolacier" />
          )}
          {!isCollapsed && userLogin && (
            <div className="profile-picture-container">
              <img
                key={imgKey}
                src={`${avatarUrl}?t=${imgKey}`}
                alt="Profil"
                className="profile-img"
                onError={e => { e.target.onerror = null; e.target.src = fallbackUrl; }}
              />
              <label className="avatar-upload-btn" title="Changer la photo">
                📷
                <input type="file" accept="image/*" onChange={handleImageUpload} hidden />
              </label>
            </div>
          )}

          <h2 className="ok-text">
            {isCollapsed ? config.clientShort : config.clientName}
          </h2>

          {!isCollapsed && (
            <div className="user-line">
              <p className="stat-label">{userName}</p>
              {isAdmin && (
                <button
                  className="settings-icon-btn"
                  onClick={() => setShowSettings(true)}
                  title="Applications visibles"
                >
                  ⚙️
                </button>
              )}
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          {allowedApps
            .filter(app => visibleApps.includes(app.key))
            .map(app => (
              <NavLink
                key={app.key}
                to={`/${app.key}`}
                className="btn nav-btn"
                title={app.label}
              >
                <span className="nav-short" aria-hidden="true">{app.label.slice(0, 1)}</span>
                <span className="nav-full">{app.label}</span>
              </NavLink>
            ))}
          {isAdmin && (
            <NavLink to="/equipe" className="btn nav-btn" title="Équipe">
              <span className="nav-short" aria-hidden="true">E</span>
              <span className="nav-full">Équipe</span>
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/vehicules" className="btn nav-btn" title="Gestion des véhicules">
              <span className="nav-short" aria-hidden="true">V</span>
              <span className="nav-full">Véhicules</span>
            </NavLink>
          )}
          {isSuperAdmin && (
            <NavLink to="/users" className="btn nav-btn superadmin-users-link" title="Gestion des utilisateurs">
              <span className="nav-short" aria-hidden="true">U</span>
              <span className="nav-full">Utilisateurs</span>
            </NavLink>
          )}
        </nav>

        <button onClick={handleLogout} className="btn logout-btn">
          <span className="nav-short" aria-hidden="true">D</span>
          <span className="nav-full">Déconnexion</span>
        </button>

      </aside>

      {isAdmin && showSettings && (
        <div className="apps-modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="apps-modal" onClick={e => e.stopPropagation()}>

            <div className="apps-modal-header">
              <h3>Applications visibles</h3>
              <button className="apps-modal-close" onClick={() => setShowSettings(false)}>✕</button>
            </div>

            <div className="apps-modal-content">
              {config.apps.map(app => (
                <label key={app.key} className="app-checkbox-line">
                  <input
                    type="checkbox"
                    checked={visibleApps.includes(app.key)}
                    onChange={() => toggleApp(app.key)}
                  />
                  <span>{app.label}</span>
                </label>
              ))}
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
